import { AixRouteService, ProfileStoreFacade } from '@advisor-ui/app-services';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    viewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { orderStatus } from '@trade-platform/lib-enums';
import {
    AixDataTestingDirective,
    AixLoadingComponent,
    AixNotificationComponent,
    BannerStylesDirective
} from '@trade-platform/ui-components';
import {
    AixErrorBoxComponent,
    AixExtendedNotification,
    AppState,
    BaseOrder,
    constants,
    DocumentFile,
    DocumentViewerDownloadService,
    isFeatureEnabled,
    OrderFormComment,
    SignatureField,
    SignaturesPreviewResponse,
    SIGNATURE_ROLE_COLORS_DARK,
    getSignatureGroupLabels
} from '@trade-platform/ui-shared';
import { getFromStorage, setToStorage } from '@trade-platform/ui-utils';
import { isInProgress, RemoteData } from 'ngx-remotedata';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { OrderFormCommentsService } from '../order-comments/order-form-comments.helper';
import {
    getDocumentIndexById,
    getFileRouteId,
    getOrderStatusIndex,
    hasEntityApprovalPermission,
    hasReadOnlyAccess,
    isAdminAndCanEditOrder,
    userSeesOrderProcess
} from '../utils';
import { getAction, getFilesToDisplay } from './document-viewer/document-viewer.helper';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../base.orders.store.facade';
import { AixDocumentTabsComponent } from './document-viewer/document-tabs/document-tabs';
import { AixDocumentViewerComponent } from './document-viewer/document-viewer';
import { AsyncPipe } from '@angular/common';
import { AixDocumentViewerActionBarComponent } from './document-viewer/document-action-bar/document-action-bar';
import { SigningMethodPreferenceService } from '../store/order-signing-method/signing-method-preference.service';

/**
 * Shown when the requested document is not on the order any more — it was replaced or removed
 * while this page held an older copy. Stated as a fact rather than a failure, because a document
 * changing is a normal thing for a teammate to have done.
 *
 * refs trade-platform/trade-platform#7593
 */
export const DOCUMENT_NO_LONGER_ON_ORDER_NOTICE =
    "That document is no longer on this order. Showing the order's current documents.";

@Component({
    selector: 'aix-order-document-viewer',
    templateUrl: './order-document-viewer.html',
    styleUrls: ['./order-document-viewer.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixLoadingComponent,
        AixDocumentViewerActionBarComponent,
        AixErrorBoxComponent,
        AixNotificationComponent,
        AixDataTestingDirective,
        AixDocumentViewerComponent,
        AixDocumentTabsComponent,
        AsyncPipe
    ],
    hostDirectives: [BannerStylesDirective]
})
export class AixOrderDocumentViewerComponent implements OnInit, OnDestroy {
    router = inject(Router);
    route = inject(ActivatedRoute);
    commentsService = inject(OrderFormCommentsService);
    store = inject<Store<AppState>>(Store);
    profileStore = inject(ProfileStoreFacade);
    ref = inject(ChangeDetectorRef);
    storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    private docDownloader = inject(DocumentViewerDownloadService);
    private routeService = inject(AixRouteService);
    private signingMethodPreference = inject(SigningMethodPreferenceService);

    actionsLoadingState$: Observable<boolean>;
    comments: OrderFormComment[];
    signaturesPreview: SignaturesPreviewResponse | null = null;

    documentId: string;
    /*
      Shown in its own notification rather than the shared `#notification` slot, which four separate
      triggers clear — the route params subscription, the comments load, onNotOnboarded and onClose.
      This message has to survive the order and comments finishing loading, so it cannot live there.
      refs trade-platform/trade-platform#7593
    */
    documentNotFound = false;
    isReadOnly: boolean | undefined | null;
    hasComments: boolean | undefined | null;
    needsClarifications: boolean | undefined | null;
    needsChanges: boolean | undefined | null;
    hiddenRoles = new Set<string>();
    roleFieldIndex = new Map<string, number>();
    wetSignOverride = false;

    // Cached derived data from signaturesPreview — recomputed in updateSignaturesCaches()
    availableRoles: {
        groupKey: string;
        role: string;
        displayName: string;
        color: string;
        isWetSign: boolean;
    }[] = [];
    signaturesForRole = new Map<string, SignatureField[]>();

    styles = {
        documentControls: {},
        loading: {
            right: '3%'
        }
    };

    activeNotification: AixExtendedNotification = {
        text: '',
        status: 'alert'
    };

    subscriptions: Subscription[] = [];
    states: Observable<RemoteData<any, any>>[];

    canClose = false;

    readonly reducerSuffix = this.storeFacade.type;
    readonly notificationLanguage = {
        nigo: () =>
            'To resolve action items please replace this document with a corrected version.',
        documentNoLongerOnOrder: () => DOCUMENT_NO_LONGER_ON_ORDER_NOTICE
    };

    notification = viewChild<AixNotificationComponent>('notification');
    resolveOfflineNotification = viewChild<AixNotificationComponent>(AixNotificationComponent);
    documentViewer = viewChild<AixDocumentViewerComponent>(AixDocumentViewerComponent);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.states = [this.storeFacade.orderFormCommentsRemoteData$];
        this.actionsLoadingState$ = combineLatest([
            this.storeFacade.orderApproveRemoteData$,
            this.storeFacade.orderSignRemoteData$,
            this.storeFacade.orderMarkNigoRemoteData$,
            this.storeFacade.orderMarkNeedsClarificationRemoteData$,
            this.storeFacade.orderMakeChangesRemoteData$
        ]).pipe(map(states => states.some(isInProgress)));

        this.storeFacade.actions.documentViewerReset.dispatch();
        this.storeFacade.actions.documentViewerSetTab.dispatch({ tab: 'Order' });
    }

    ngOnInit() {
        this.subscriptions.push(
            this.route.params.pipe(first()).subscribe(params => {
                const orderId = params['orderId'];
                this.storeFacade.actions.getOrder.dispatch({
                    orderId,
                    reducerSuffix: this.reducerSuffix
                });
            }),
            this.route.params.subscribe(params => {
                const docId = params['documentId'];
                this.closeNotification();

                if (docId) {
                    this.documentId = docId;

                    const docState = this.storeFacade.documentViewer;
                    if (docState.initialized) {
                        this.onDocumentViewerInitialized({ userNavigated: true });
                    }
                }
            }),
            this.storeFacade.orderSuccess$.subscribe(order => {
                const profile = this.profileStore.profile;

                const documentViewAction = getAction(order);
                this.storeFacade.actions.documentViewerSetAction.dispatch({
                    action: documentViewAction
                });

                const hasPermission = !!this.hasPermission(documentViewAction);
                this.storeFacade.actions.documentViewerSetPermissions.dispatch({
                    permissions: hasPermission
                });

                this.isReadOnly =
                    hasReadOnlyAccess(profile, order.firm.id) &&
                    getOrderStatusIndex(order.status) <
                        getOrderStatusIndex(orderStatus.pendingFirmApproval) &&
                    getOrderStatusIndex(order.status) !==
                        getOrderStatusIndex(orderStatus.pendingPresignatureReview);

                // Load signatures preview only for review order and collect signatures stages
                if (
                    isFeatureEnabled('previewSignatures') &&
                    (order.status === orderStatus.readyForPresignatureReview ||
                        order.status === orderStatus.pendingPresignatureReview ||
                        order.status === orderStatus.presignatureReviewComplete ||
                        order.status === orderStatus.readyToSend ||
                        order.status === orderStatus.pendingSignatures)
                ) {
                    this.loadSignaturesPreview(order.id);
                }

                /*
                  pdf.js usually finishes loading before the order request comes back, so the first
                  resolution pass ran with no files and could conclude nothing. Resolve again now
                  the order is here — otherwise the route's document id is never applied and every
                  deep link silently opens the first document instead.
                  refs trade-platform/trade-platform#7593
                */
                if (this.storeFacade.documentViewer.initialized) {
                    this.onDocumentViewerInitialized();
                }
            }),
            this.storeFacade.actions.documentViewerShowNotification.success$.subscribe(action => {
                this.openNotification(action.payload.notification);
            }),
            this.storeFacade.actions.needsClarification.success$.subscribe(action => {
                this.router.navigate(this.storeFacade.routes.status());
            }),
            this.storeFacade.actions.resumeApprovals.success$.subscribe(action => {
                this.router.navigate(this.storeFacade.routes.status());
            }),
            this.storeFacade.actions.markNigo.success$.subscribe(action => {
                this.router.navigate(this.storeFacade.routes.status());
            }),
            this.storeFacade.orderMakeChangesSuccess$.subscribe(order => {
                // If make changes api call successful, re-route to order process;
                if (!!this.storeFacade.order && order.id === this.storeFacade.order.id) {
                    this.onClose();
                    this.router.navigate(
                        this.storeFacade.routes.process.overview(this.storeFacade.order.id)
                    );
                }
            }),
            this.storeFacade.actions.documentViewerSetDocument.success$.subscribe(action => {
                const order = this.storeFacade.order;
                const docState = this.storeFacade.documentViewer;
                const filesToDisplay = docState.filesToDisplay;
                const index = getDocumentIndexById(filesToDisplay, action.payload.currentDocument);
                const currentFile = filesToDisplay[index];
                const currentForm = docState.currentForm;

                if (order && order.forms && docState && currentFile && currentFile.formId) {
                    const form = currentFile ? order.forms[currentFile.formId] : currentForm;
                    if (
                        form &&
                        form.isOnboarded === false &&
                        getOrderStatusIndex(order.status) <
                            getOrderStatusIndex(orderStatus.readyToSend) &&
                        order.status !== orderStatus.pendingPresignatureReview
                    ) {
                        this.onNotOnboarded(form.id);
                    }
                }
            }),
            this.storeFacade.actions.documentViewerSetInitialized.success$.subscribe(_ => {
                this.onDocumentViewerInitialized();
            }),
            this.storeFacade.actions.getFormComments.success$.subscribe(comments => {
                // Since there's a delayed data load, trigger not onboarded notification logic once comments load, if necessary;
                const docState = this.storeFacade.documentViewer;
                const order = this.storeFacade.order;
                const form =
                    docState.currentFile && docState.currentFile.formId
                        ? order.forms[docState.currentFile.formId]
                        : docState.currentForm;
                if (
                    form &&
                    form.isOnboarded === false &&
                    getOrderStatusIndex(order.status) <
                        getOrderStatusIndex(orderStatus.readyToSend) &&
                    order.status !== orderStatus.pendingPresignatureReview
                ) {
                    this.onNotOnboarded(form.id);
                }

                this.comments = this.commentsService.getComments(form ? form.id : null);

                let reviewer = false;
                this.profileStore.profile.roles
                    .map(role => role.name)
                    .some(role => {
                        if (role === 'reviewer') {
                            reviewer = true;
                        }
                    });

                if (!reviewer && order.status !== orderStatus.completed) {
                    this.hasComments = false;
                    for (const comment of comments.payload) {
                        if (
                            (comment.children && comment.children.length > 0) ||
                            comment.commentType === 'UserGenerated'
                        ) {
                            const hasUnresolvedComments =
                                comment.status.toUpperCase() === 'UNRESOLVED';
                            const hasUnresolvedChildComments = comment.children.some(
                                (child: OrderFormComment) =>
                                    child.status.toUpperCase() === 'UNRESOLVED'
                            );
                            if (hasUnresolvedComments) {
                                if (
                                    comment.commentTag === 'clarification' &&
                                    hasUnresolvedChildComments
                                ) {
                                    this.hasComments = true;
                                    this.needsClarifications = true;
                                    this.closeNotification();
                                } else if (comment.commentTag === 'clarification') {
                                    this.hasComments = false;
                                    this.needsClarifications = false;
                                    break;
                                }
                                if (comment.commentTag === 'change') {
                                    this.needsChanges = true;
                                }
                            }
                        }
                    }
                }
            })
        );

        this.cachePreviousUrl();
    }

    cachePreviousUrl() {
        const previousUrl = this.routeService.previousRoute;
        if (previousUrl && !/document-viewer/.test(previousUrl)) {
            setToStorage('viewerCloseUrl', previousUrl);
        }
    }

    /**
     * @param userNavigated true when a route change brought us here, so the user asked for this
     * document; false for a background pass (pdf.js initialising, the order reloading).
     */
    onDocumentViewerInitialized({ userNavigated = false }: { userNavigated?: boolean } = {}) {
        const order = this.storeFacade.order;
        const filesToDisplay = getFilesToDisplay(order);
        const index = getDocumentIndexById(filesToDisplay, this.documentId);
        const requestedFile = index >= 0 ? filesToDisplay[index] : undefined;

        if (requestedFile) {
            /*
              A url may arrive carrying either identity — links made before this change hold a
              File.id — so settle on the stable one here. Everything downstream then compares like
              with like, and a link that keeps working today still works after the file is next
              replaced. refs trade-platform/trade-platform#7593
            */
            const routeId = getFileRouteId(requestedFile);
            const wasLegacyId = routeId !== this.documentId;
            this.documentId = routeId;

            /*
              Retract a notice raised by an earlier pass. A first pass can legitimately miss and
              explain itself — finalize deletes an order's form files and fires the regeneration
              without awaiting it, so just after a re-finalize a form sits on the order with no
              document behind it. When the order reloads and the file has landed, the explanation is
              no longer true.

              markForCheck is not optional here. This component is OnPush, so clearing the flag on
              its own never re-evaluates the template: the notice stayed painted over a document
              that had by then resolved correctly, which reads as a failed load of a file that is
              plainly on screen. activateFirstDocument marks for check when it raises the notice;
              this is the matching retraction.
            */
            const wasExplained = this.documentNotFound;
            this.documentNotFound = false;
            if (wasExplained) {
                this.ref.markForCheck();
            }

            this.storeFacade.actions.documentViewerSetDocument.dispatch({
                currentDocument: routeId
            });
            this.storeFacade.actions.documentViewerSetFile.dispatch({
                currentFile: requestedFile
            });

            if (wasLegacyId) {
                this.router.navigate(this.storeFacade.routes.documentViewer(order.id, routeId), {
                    replaceUrl: true
                });
            }
            return;
        }

        /*
          Don't move a user who is reading a document that is still on the order — a background
          reload should not yank them to document one.

          Two things narrow that. The document has to still be there: a supporting document is
          replaced rather than updated, so after a reload the file on screen can be absent from the
          fresh list, and staying put would pin the user to a file the download endpoint 404s.

          And the user must not have asked to be here. Angular reuses this component across a
          documentId param change, so a route change lands here with a document already on screen —
          Back to a stale link, or a pasted url. Suppressing the fallback then leaves the url naming
          one document and the pane showing another, with nothing to say so.

          refs trade-platform/trade-platform#7593
        */
        const currentFile = this.storeFacade.documentViewer?.currentFile;
        const currentFileIsStillOnOrder =
            !!currentFile && getDocumentIndexById(filesToDisplay, getFileRouteId(currentFile)) >= 0;

        if (!userNavigated && currentFileIsStillOnOrder) {
            return;
        }

        /*
          `documentViewerSetInitialized` fires whether or not a document was asked for, so this also
          covers the `:orderId/document-viewer` route, which carries no id. Opening the first
          document is the normal entry there — only an id that was requested and cannot be found is
          worth explaining.
        */
        this.activateFirstDocument(order, filesToDisplay, {
            requestedDocumentMissing: !!this.documentId
        });
    }

    /**
     * Opens the order's first document. When the user asked for a specific document that is no
     * longer on the order — its file was replaced or removed while this page held an older copy —
     * also drop the stale id from the url and say why they are somewhere else.
     *
     * refs trade-platform/trade-platform#7593
     */
    private activateFirstDocument(
        order: BaseOrder,
        filesToDisplay: DocumentFile[],
        { requestedDocumentMissing }: { requestedDocumentMissing: boolean }
    ) {
        const firstFile = filesToDisplay[0];
        if (!firstFile) {
            return;
        }

        /*
          `documentId` keeps holding the id that was asked for, matching the url, which is not
          rewritten either. Were it to hold the fallback's id instead, the next order reload would
          resolve it successfully and clear the notice with the user having done nothing —
          retracting the explanation while they are still on a document they did not choose.
        */
        const firstId = getFileRouteId(firstFile);

        this.storeFacade.actions.documentViewerSetDocument.dispatch({
            currentDocument: firstId
        });
        this.storeFacade.actions.documentViewerSetFile.dispatch({ currentFile: firstFile });

        if (!requestedDocumentMissing) {
            return;
        }

        /*
          Explain, and deliberately do not rewrite the url. Rewriting changes the documentId route
          param, and the params subscription in ngOnInit closes any open notification on every param
          change — so rewriting and explaining are mutually exclusive here, and the explanation is
          worth more: without it the user is silently looking at a different document.

          Deferring the notice until navigation resolved was tried and does not work; route params
          emit after the promise, so the notice opened and was closed again. Leaving the dead id in
          the url is also honest — the link really is dead, and a refresh explains itself again.

          A live legacy file id is still normalised and rewritten above; that path raises no notice.
        */
        this.documentNotFound = true;
        this.ref.markForCheck();
    }

    hasPermission(documentViewAction: string) {
        const order = this.storeFacade.order;
        const profile = this.profileStore.profile;
        const roles = profile ? profile.roles.map(role => role.name) : [];

        switch (documentViewAction) {
            case 'sign':
            case 'wetSign':
                return (
                    roles.indexOf(constants.profileTypes.REVIEWER) > -1 &&
                    roles.indexOf(constants.profileTypes.SIGNER) > -1 &&
                    hasEntityApprovalPermission(order.currentApproval, profile)
                );
            case 'approve':
                return (
                    roles.indexOf(constants.profileTypes.REVIEWER) > -1 &&
                    roles.indexOf(constants.profileTypes.AUTHORIZER) > -1 &&
                    hasEntityApprovalPermission(order.currentApproval, profile)
                );
            case 'signed':
            case 'resolve':
                const isValidRole =
                    roles.indexOf(constants.profileTypes.ADMIN) > -1 ||
                    roles.indexOf(constants.profileTypes.ACTIVE_INVESTOR) > -1 ||
                    roles.indexOf(constants.profileTypes.ADVISOR) > -1 ||
                    roles.indexOf(constants.profileTypes.ASSISTANT) > -1;

                // permission to edit submitted orders
                if (
                    (roles.indexOf(constants.profileTypes.REVIEWER) > -1 || isValidRole) &&
                    order.status === orderStatus.submitted &&
                    isFeatureEnabled('editSubmittedOrders')
                ) {
                    return true;
                }

                if (
                    order.status === orderStatus.submitted &&
                    !isFeatureEnabled('editSubmittedOrders')
                ) {
                    return false;
                }

                // It's an investor, advisor or assistant and the order is Under Review
                if (
                    isValidRole &&
                    order.status !== orderStatus.pendingPresignatureReview &&
                    getOrderStatusIndex(order.status) >=
                        getOrderStatusIndex(orderStatus.pendingFirmApproval) &&
                    getOrderStatusIndex(order.status) !== getOrderStatusIndex(orderStatus.nigo) &&
                    getOrderStatusIndex(order.status) !==
                        getOrderStatusIndex(orderStatus.needsClarification) &&
                    getOrderStatusIndex(order.status) !==
                        getOrderStatusIndex(orderStatus.offline) &&
                    getOrderStatusIndex(order.status) !==
                        getOrderStatusIndex(orderStatus.submitted) &&
                    !this.isRiaSubmission(order)
                ) {
                    return false;
                }

                if (
                    isAdminAndCanEditOrder(profile, order.firm.id) &&
                    (getOrderStatusIndex(order.status) === getOrderStatusIndex(orderStatus.nigo) ||
                        getOrderStatusIndex(order.status) ===
                            getOrderStatusIndex(orderStatus.needsClarification))
                ) {
                    return true;
                }

                return isValidRole;
            default:
                return false;
        }
    }

    isRiaSubmission(order: BaseOrder) {
        return (
            (order.status === orderStatus.pendingManualSubmission ||
                order.status === orderStatus.pendingSubmissionToTransferAgent) &&
            order.approvals?.length === 0
        );
    }

    onNotOnboarded(currentFormId: string) {
        this.closeNotification();

        const formComments = this.commentsService.getComments(currentFormId);
        if (formComments.length > 0) {
            this.storeFacade.actions.documentViewerSetTab.dispatch({ tab: 'Comments' });
            this.storeFacade.actions.documentViewerShowNotification.dispatch({
                notification: {
                    text: this.notificationLanguage.nigo(),
                    status: 'warning'
                }
            });
        }
    }

    onClickDownload() {
        const order = this.storeFacade.order;
        this.docDownloader.getDocument({
            href: `${this.storeFacade.apiUrl}/${order.id}/download/`,
            name: order.title
        });
    }

    onClickDownloadDocument(fileId: string) {
        const order = this.storeFacade.order;
        /*
          The download url has to carry a file id that is still on the order. `fileId` may be a
          stable `formId` or an id captured before the file was replaced, so resolve it against
          the order rather than passing it straight through.
          refs trade-platform/trade-platform#7593
        */
        const file = order.files.find(
            orderFile => orderFile.id === fileId || orderFile.formId === fileId
        );
        if (!file) {
            // Say something rather than nothing: a button that silently does nothing reads as a
            // dead click, and the order in view is out of date.
            this.storeFacade.actions.documentViewerShowNotification.dispatch({
                notification: {
                    text: DOCUMENT_NO_LONGER_ON_ORDER_NOTICE,
                    status: 'alert'
                }
            });
            return;
        }

        this.docDownloader.getDocument({
            href: `${this.storeFacade.apiUrl}/${order.id}/download/${file.id}`,
            name: file.screenName || file.name || ''
        });
    }

    onClose() {
        this.storeFacade.actions.documentViewerSetTab.dispatch({ tab: 'Order' });
        this.closeNotification();
    }

    closeNotification() {
        if (this.notification()?.isOpen()) {
            this.activeNotification = {
                text: '',
                status: 'alert'
            };
            this.styles.documentControls = {};
            this.notification()?.closeNotification();
            this.ref.detectChanges();
        }
    }

    openNotification(notification: AixExtendedNotification) {
        this.activeNotification = notification;
        this.styles.documentControls = {
            bottom: '40px'
        };
        this.notification()?.openNotification();
        this.ref.detectChanges();
    }

    onCloseNotification() {
        this.styles.documentControls = {
            bottom: '0'
        };
    }

    onClickClose() {
        const closeUrl = getFromStorage('viewerCloseUrl') as string;
        if (closeUrl && !closeUrl.includes('access_token')) {
            this.router.navigateByUrl(closeUrl);
        } else {
            const order = this.storeFacade.order;
            const profile = this.profileStore.profile;

            if (userSeesOrderProcess(profile, order.status, order.repCode, order.firm.id)) {
                this.router.navigate(this.storeFacade.routes.process.overview(order.id));
            } else {
                this.router.navigate(this.storeFacade.routes.detailOverview(order.id));
            }
        }
    }

    returnOrder() {
        this.storeFacade.actions.documentViewerHideNotification.dispatch();
        const order = this.storeFacade.order;
        this.storeFacade.actions.resumeApprovals.dispatch({
            orderId: order.id
        });
    }

    editOrder() {
        this.storeFacade.actions.documentViewerHideNotification.dispatch();
        const order = this.storeFacade.order;
        this.storeFacade.actions.makeChanges.dispatch({ orderId: order.id });
    }

    loadSignaturesPreview(orderId: string) {
        this.subscriptions.push(
            this.storeFacade
                .getSignaturesPreview(orderId)
                .pipe(first())
                .subscribe({
                    next: response => {
                        this.signaturesPreview = response;
                        this.updateSignaturesCaches();
                        this.ref.markForCheck();
                    },
                    error: err => {
                        console.error('Failed to load signatures preview:', err);
                        this.signaturesPreview = null;
                        this.updateSignaturesCaches();
                        this.ref.markForCheck();
                    }
                })
        );
    }

    private computeWetSignOverride(): boolean {
        const order = this.storeFacade.order;
        if (!order) {
            return false;
        }

        const isReadyToSendOrLater =
            getOrderStatusIndex(order.status) >= getOrderStatusIndex(orderStatus.readyToSend);

        let isSelected: boolean | undefined;
        if (isReadyToSendOrLater) {
            const pref = this.signingMethodPreference.currentPreference();
            if (pref === 'eSign') {
                isSelected = true;
            } else if (pref === 'wetsign') {
                isSelected = false;
            } else {
                isSelected = order.eSign?.isSelected;
            }
        } else {
            isSelected = order.eSign?.isSelected;
        }

        const isAvailable = order.eSign?.isAvailable;
        const acceptedByUser = order.eSign?.acceptedByUser;

        return isSelected === false || isAvailable === false || acceptedByUser === false;
    }

    private updateSignaturesCaches() {
        this.roleFieldIndex.clear();
        if (!this.signaturesPreview) {
            this.availableRoles = [];
            this.signaturesForRole = new Map();
            this.wetSignOverride = false;
            return;
        }
        const wetSignOverride = this.computeWetSignOverride();
        this.wetSignOverride = wetSignOverride;
        // Single source of truth for each group's resolved role + label, shared with the PDF overlay.
        const groupLabels = getSignatureGroupLabels(this.signaturesPreview);
        this.availableRoles = Object.entries(this.signaturesPreview).map(([key, group], idx) => {
            const { role, displayName } = groupLabels.get(key) ?? { role: key, displayName: key };
            return {
                groupKey: key,
                role,
                displayName,
                color: SIGNATURE_ROLE_COLORS_DARK[idx % SIGNATURE_ROLE_COLORS_DARK.length],
                isWetSign: wetSignOverride || group.wetSign
            };
        });
        this.signaturesForRole = new Map();
        for (const [key, group] of Object.entries(this.signaturesPreview)) {
            this.signaturesForRole.set(key, group.signatures);
        }
    }

    onToggleRole(role: string) {
        if (this.hiddenRoles.has(role)) {
            this.hiddenRoles.delete(role);
        } else {
            this.hiddenRoles.add(role);
        }
        this.hiddenRoles = new Set(this.hiddenRoles); // Trigger change detection
        this.ref.markForCheck();
    }

    onNavigateToSignature(field: SignatureField) {
        const order = this.storeFacade.order;
        const docState = this.storeFacade.documentViewer;
        const filesToDisplay = docState.filesToDisplay;

        // Find the file that matches the signature field's documentId (which is formId)
        const targetFile = filesToDisplay.find(file => file.formId === field.documentId);

        // No displayed file/form matches this signature's documentId — nothing to navigate to.
        if (!targetFile) {
            return;
        }

        // Compare route ids, not file ids: the route carries the stable form id, so comparing
        // against `targetFile.id` would never match and would re-navigate needlessly. refs #7593
        const targetRouteId = getFileRouteId(targetFile);

        if (targetRouteId !== docState.currentDocument) {
            // Set pending navigation so document-viewer scrolls after PDF loads
            this.documentViewer()?.setPendingSignatureNavigation(field);
            // Navigate to the document
            this.router.navigate(this.storeFacade.routes.documentViewer(order.id, targetRouteId));
        } else {
            // Already on the correct document, scroll to signature
            this.documentViewer()?.scrollToSignature(field);
        }
    }

    getRoleFieldIndex(role: string): number {
        return this.roleFieldIndex.get(role) ?? 0;
    }

    onPrevSignature(role: string) {
        const signatures = this.signaturesForRole.get(role) ?? [];
        if (signatures.length === 0) return;

        let currentIndex = this.roleFieldIndex.get(role) ?? 0;
        currentIndex = currentIndex > 0 ? currentIndex - 1 : signatures.length - 1;
        this.roleFieldIndex.set(role, currentIndex);
        this.roleFieldIndex = new Map(this.roleFieldIndex);
        this.onNavigateToSignature(signatures[currentIndex]);
    }

    onNextSignature(role: string) {
        const signatures = this.signaturesForRole.get(role) ?? [];
        if (signatures.length === 0) return;

        let currentIndex = this.roleFieldIndex.get(role) ?? 0;
        currentIndex = currentIndex < signatures.length - 1 ? currentIndex + 1 : 0;
        this.roleFieldIndex.set(role, currentIndex);
        this.roleFieldIndex = new Map(this.roleFieldIndex);
        this.onNavigateToSignature(signatures[currentIndex]);
    }

    ngOnDestroy() {
        this.storeFacade.actions.resetOrder.dispatch({ reducerSuffix: this.reducerSuffix });
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
